import { Progress } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { FontStyle, Text } from '~/components/core'
import { useAnnounce } from '~/components/shared/LiveAnnouncer'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { domains } from '~/constants/domains'
import urls from '~/constants/urls'
import {
  usePartnerDesktopAppName,
  usePartnerLogo,
} from '~/hooks/useCurrentPartner'
import {
  ContentWrapper,
  LoginButtonWrapper,
  LogoImage,
} from '~/pages/Login/Login.styles'
import { LoginBackground } from '~/pages/Login/LoginBackground'
import {
  ActionStack,
  CopyStack,
  FallbackStack,
  wrappingCopyStyle,
} from '~/pages/OpenApp/OpenApp.styles'
import { useElectronUpdaterContext } from '~/providers/ElectronUpdaterContext'
import { Size } from '~/types/ThemeTypes'
import { openInNewTab } from '~/utils/functions'

import type { UpdateRequirement } from '@mangovoice/types'

const RECHECK_INTERVAL_MS = 15 * 60 * 1000
// No download progress for this long falls back to the manual installer.
const STALLED_DOWNLOAD_MS = 2 * 60 * 1000

const TITLE_ID = 'required-update-title'

// Scrolls the card over the fixed background, so the escape links stay
// reachable at high zoom or in a short window. Pointer events pass through to
// the region selector behind it; the card takes them back.
const ScrollArea = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: safe center;
  overflow-y: auto;
  padding: 4rem 1rem;
  box-sizing: border-box;
  pointer-events: none;
`

// Taller than the login form: no height cap, so nothing overflows the card.
const BlockingCard = styled(ContentWrapper)`
  max-height: none;
  flex-shrink: 0;
  pointer-events: auto;
`

const Title = styled.h1`
  margin: 0;
  font-family: 'Inter', Helvetica, sans-serif;
  font-size: 20px;
  font-weight: normal;
  line-height: 24px;
  text-align: center;
  color: var(--chakra-colors-fg-default);

  /* Focused only by script so screen readers start here; it is not a control. */
  &:focus {
    outline: none;
  }
`

interface RequiredUpdateScreenProps {
  requirement: UpdateRequirement
}

export const RequiredUpdateScreen = ({
  requirement,
}: RequiredUpdateScreenProps) => {
  const { t } = useTranslation()
  const appName = usePartnerDesktopAppName()
  const logo = usePartnerLogo()
  const {
    checkForUpdates,
    quitAndInstall,
    isUpdateDownloaded,
    isUpdateBlocked,
    isUpdateNotAvailable,
    downloadPercent,
    updateBlockedDownloadUrl,
    updatePermissionHelpUrl,
    updateIgnoringInstallPermission,
    lastCheckStatus,
    lastInstallOutcome,
  } = useElectronUpdaterContext()
  const [isLinkCopied, setIsLinkCopied] = useState(false)
  const [isDownloadStalled, setIsDownloadStalled] = useState(false)
  const checkForUpdatesRef = useRef(checkForUpdates)
  checkForUpdatesRef.current = checkForUpdates
  const announce = useAnnounce()
  const titleRef = useRef<HTMLHeadingElement>(null)

  // The first check makes main replay `update-downloaded` when the download
  // finished before this window started to listen. Later checks also fetch
  // channels.json again, so a cleared minimumVersion lifts the block sooner
  // than the normal three-hour schedule.
  useEffect(() => {
    checkForUpdatesRef.current()
    const interval = setInterval(
      () => checkForUpdatesRef.current(),
      RECHECK_INTERVAL_MS,
    )
    return () => clearInterval(interval)
  }, [])

  const downloadUrl = requirement.downloadUrl ?? updateBlockedDownloadUrl
  // An install ran on the last launch but this version is still running:
  // usually an IT policy or a declined administrator prompt.
  const installDidNotApply = lastInstallOutcome?.status === 'not_applied'
  const isDownloading = downloadPercent !== null
  // A published build older than minimumVersion reports update-not-available,
  // so the in-app update can never finish.
  const needsManualInstall =
    isUpdateBlocked ||
    isUpdateNotAvailable ||
    installDidNotApply ||
    isDownloadStalled ||
    (!isDownloading && lastCheckStatus === 'failed')

  // Restarts on every progress event, so this covers a download that never
  // starts and one that freezes partway.
  useEffect(() => {
    setIsDownloadStalled(false)
    if (isUpdateDownloaded) {
      return
    }
    const timer = setTimeout(
      () => setIsDownloadStalled(true),
      STALLED_DOWNLOAD_MS,
    )
    return () => clearTimeout(timer)
  }, [downloadPercent, isUpdateDownloaded])

  const copyDownloadLink = () => {
    if (!downloadUrl) {
      return
    }
    window.electron?.clipboard?.writeText(downloadUrl)
    setIsLinkCopied(true)
  }

  const renderManualInstallActions = () =>
    downloadUrl && (
      <>
        <MangoButton onClick={() => openInNewTab(downloadUrl)} size={Size.md}>
          {t('updateRequirement.downloadInstaller', 'Download installer')}
        </MangoButton>
        <MangoButton
          variant={MangoButtonVariant.Brand_Text}
          size={Size.sm}
          onClick={copyDownloadLink}
        >
          {isLinkCopied
            ? t('updateRequirement.linkCopied', 'Link copied')
            : t('updateRequirement.copyDownloadLink', 'Copy download link')}
        </MangoButton>
      </>
    )

  // The screen replaces the router, which would otherwise leave focus on
  // <body>. Focusing the title reads it; the polite message then follows it
  // instead of cutting it off.
  useEffect(() => {
    titleRef.current?.focus()
    announce(
      t(
        'updateRequirement.alertBody',
        'Calls go to other devices or voicemail until you update.',
      ),
    )
  }, [])

  // Only state changes are announced; the progress bar exposes the percentage.
  useEffect(() => {
    if (isUpdateDownloaded) {
      announce(t('updateRequirement.restartToUpdate', 'Restart to update'))
    }
  }, [isUpdateDownloaded])

  useEffect(() => {
    if (updatePermissionHelpUrl) {
      announce(t('updatePermissionBanner.message'))
    }
  }, [updatePermissionHelpUrl])

  useEffect(() => {
    if (needsManualInstall) {
      announce(
        installDidNotApply || isUpdateBlocked
          ? t('updateBlockedBanner.message')
          : t(
              'updateRequirement.manualInstallMessage',
              'The app cannot install the update automatically. Download and install the latest version.',
            ),
      )
    }
  }, [needsManualInstall])

  const renderStatus = () => {
    if (isUpdateDownloaded) {
      return (
        <>
          {installDidNotApply && (
            <Text
              fontStyle={FontStyle.Small}
              align="center"
              style={wrappingCopyStyle}
              text={t('updateBlockedBanner.message')}
            />
          )}
          <MangoButton onClick={quitAndInstall} size={Size.md}>
            {t('updateRequirement.restartToUpdate', 'Restart to update')}
          </MangoButton>
          {installDidNotApply && renderManualInstallActions()}
        </>
      )
    }
    // Main skips the download on a Mac where the user cannot write to the
    // installed app, so offer the admin-password route and the help article.
    if (updatePermissionHelpUrl) {
      return (
        <>
          <Text
            fontStyle={FontStyle.Small}
            align="center"
            style={wrappingCopyStyle}
            text={t('updatePermissionBanner.message')}
          />
          <MangoButton onClick={updateIgnoringInstallPermission} size={Size.md}>
            {t('updatePermissionBanner.updateAnyway')}
          </MangoButton>
          <MangoButton
            variant={MangoButtonVariant.Brand_Text}
            size={Size.sm}
            onClick={() => openInNewTab(updatePermissionHelpUrl)}
          >
            {t('updatePermissionBanner.howToFix')}
          </MangoButton>
        </>
      )
    }
    if (needsManualInstall) {
      return (
        <>
          <Text
            fontStyle={FontStyle.Small}
            align="center"
            style={wrappingCopyStyle}
            text={
              installDidNotApply || isUpdateBlocked
                ? t('updateBlockedBanner.message')
                : t(
                    'updateRequirement.manualInstallMessage',
                    'The app cannot install the update automatically. Download and install the latest version.',
                  )
            }
          />
          {renderManualInstallActions()}
        </>
      )
    }
    return (
      <>
        <Text
          fontStyle={FontStyle.Small}
          align="center"
          text={
            downloadPercent === null
              ? t('updateRequirement.preparing', 'Preparing the update...')
              : t('updateRequirement.downloadingProgress', {
                  percent: downloadPercent.toFixed(0),
                  defaultValue: 'Downloading the update: {{percent}}%',
                })
          }
        />
        <Progress.Root size="sm" width="100%" value={downloadPercent}>
          {/* The track has role="progressbar", so it needs the name. */}
          <Progress.Track
            borderRadius="md"
            aria-label={t(
              'updateRequirement.progressLabel',
              'Update download progress',
            )}
          >
            <Progress.Range />
          </Progress.Track>
        </Progress.Root>
      </>
    )
  }

  return (
    <LoginBackground showUpdaterButton={false}>
      <ScrollArea>
        <BlockingCard as="main" aria-labelledby={TITLE_ID}>
          <LogoImage alt="" src={logo} />
          <CopyStack>
            <Title id={TITLE_ID} ref={titleRef} tabIndex={-1}>
              {t('updateRequirement.requiredTitle', 'Update required')}
            </Title>
            <Text
              fontStyle={FontStyle.Small}
              align="center"
              style={wrappingCopyStyle}
              text={t('updateRequirement.requiredMessage', {
                appName,
                currentVersion: requirement.currentVersion,
                defaultValue:
                  'This version of {{appName}} ({{currentVersion}}) is no longer supported. Update to continue to use the app.',
              })}
            />
            <Text
              fontStyle={FontStyle.Small}
              align="center"
              color="var(--chakra-colors-fg-muted)"
              style={wrappingCopyStyle}
              text={t(
                'updateRequirement.callsNotice',
                'Until you update, this app cannot receive calls. Calls go to your other devices, ring groups, or voicemail.',
              )}
            />
          </CopyStack>
          <LoginButtonWrapper>
            <ActionStack>{renderStatus()}</ActionStack>
          </LoginButtonWrapper>
          <FallbackStack>
            {downloadUrl && !needsManualInstall && (
              <MangoButton
                variant={MangoButtonVariant.Brand_Text}
                size={Size.sm}
                onClick={() => openInNewTab(downloadUrl)}
              >
                {t('updateRequirement.downloadInstaller', 'Download installer')}
              </MangoButton>
            )}
            <MangoButton
              variant={MangoButtonVariant.Brand_Text}
              size={Size.sm}
              onClick={() => openInNewTab(domains.WEB_APP())}
            >
              {t('updateRequirement.useWebApp', 'Use the web app')}
            </MangoButton>
            <MangoButton
              variant={MangoButtonVariant.Brand_Text}
              size={Size.sm}
              onClick={() => openInNewTab(urls.SUPPORT)}
            >
              {t('updateRequirement.contactSupport', 'Contact support')}
            </MangoButton>
          </FallbackStack>
        </BlockingCard>
      </ScrollArea>
    </LoginBackground>
  )
}
